import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { timesheetApi, payrollApi } from '../../api/payroll';
import { projectsApi } from '../../api/projects';
import { programmesApi } from '../../api/programmes';
import { ApiError } from '../../api/client';
import { StatusBadge } from '../../components/StatusBadge';
import { IconActionButton } from '../../components/IconActionButton';
import { Send, Trash2 } from 'lucide-react';
import { DataTable } from '../../components/DataTable';
import { ReturnedBanner } from '../../components/ReturnedBanner';
import { useToast } from '../../components/Toast';

const STATUS_LABELS: Record<string, string> = { draft: 'Draft', submitted: 'Submitted', returned: 'Returned for Correction', approved: 'Approved', rejected: 'Rejected' };

export function MyPayrollPage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const timesheetsQuery = useQuery({ queryKey: ['my-timesheets'], queryFn: () => timesheetApi.mine() });
  const payslipsQuery = useQuery({ queryKey: ['my-payslips'], queryFn: () => payrollApi.myRecords() });
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const submitMutation = useMutation({
    mutationFn: (id: number) => timesheetApi.submitMine(id, { daysWorked: 0 }),
    onSuccess: () => { showSuccess('Timesheet submitted.'); queryClient.invalidateQueries({ queryKey: ['my-timesheets'] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to submit timesheet.'),
  });

  return (
    <div className="page">
      <Link to="/staff">&larr; Back to Dashboard</Link>
      <h1>My Timesheets &amp; Payslips</h1>

      <section className="card">
        <h2>My Timesheets</h2>
        {timesheetsQuery.data && (
          <DataTable
            rows={timesheetsQuery.data.data}
            rowKey={(t) => t.id}
            emptyMessage="No timesheets yet."
            columns={[
              { header: 'Month', render: (t) => t.period_month },
              { header: 'Expected Days', render: (t) => t.days_expected },
              { header: 'Days Worked', render: (t) => t.days_worked ?? '-' },
              { header: 'Overtime', render: (t) => t.overtime_hours ?? '-' },
              { header: 'Status', render: (t) => <StatusBadge status={t.status} label={STATUS_LABELS[t.status]} /> },
              {
                header: '',
                render: (t) => (
                  <span className="row-actions">
                    <button className="link-button" onClick={() => setExpandedId(expandedId === t.id ? null : t.id)}>{expandedId === t.id ? 'Hide Entries' : 'View / Add Entries'}</button>
                    {t.status === 'draft' && <IconActionButton icon={Send} label="Submit" disabled={submitMutation.isPending} onClick={() => submitMutation.mutate(t.id)} />}
                    <a href={timesheetApi.downloadMineUrl(t.id, 'pdf')} target="_blank" rel="noreferrer">PDF</a>
                    <a href={timesheetApi.downloadMineUrl(t.id, 'excel')} target="_blank" rel="noreferrer">Excel</a>
                  </span>
                ),
              },
            ]}
          />
        )}
        {timesheetsQuery.data?.data.filter((t) => t.status === 'returned').map((t) => (
          <ReturnedBanner key={t.id} returnComment={t.return_comment} resubmit={() => timesheetApi.resubmit(t.id)} queryKeysToInvalidate={[['my-timesheets']]} />
        ))}
        {expandedId && <TimesheetEntriesPanel timesheetId={expandedId} editable={timesheetsQuery.data?.data.find((t) => t.id === expandedId)?.status === 'draft'} />}
      </section>

      <section className="card">
        <h2>My Payslips</h2>
        {payslipsQuery.data && (
          <DataTable
            rows={payslipsQuery.data.data}
            rowKey={(p) => p.id}
            emptyMessage="No payslips yet."
            columns={[
              { header: 'Month', render: (p) => p.period_month ?? '-' },
              { header: 'Gross', render: (p) => `${p.currency} ${Number(p.gross_salary).toLocaleString()}` },
              { header: 'Net', render: (p) => `${p.currency} ${Number(p.net_salary).toLocaleString()}` },
              { header: 'Status', render: (p) => <StatusBadge status={p.status} /> },
              { header: '', render: (p) => <a href={payrollApi.payslipMineUrl(p.id)} target="_blank" rel="noreferrer">Download Payslip</a> },
            ]}
          />
        )}
      </section>
    </div>
  );
}

/**
 * Real day-grouped, multi-project entry support - matches the
 * spec's exact example (WASH 08:00-12:00, Resilience 13:00-17:00 as
 * two distinct rows on the same day). Add Entry is only enabled
 * while the timesheet is genuinely still in draft.
 */
function TimesheetEntriesPanel({ timesheetId, editable }: { timesheetId: number; editable: boolean }) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const entriesQuery = useQuery({ queryKey: ['timesheet-entries', timesheetId], queryFn: () => timesheetApi.listEntries(timesheetId) });
  const projectsQuery = useQuery({ queryKey: ['projects-list'], queryFn: () => projectsApi.list(), enabled: editable });
  const programmesQuery = useQuery({ queryKey: ['programmes-list'], queryFn: () => programmesApi.list(), enabled: editable });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<{ entryDate: string; startTime?: string; endTime?: string; projectId?: string; programmeId?: string; activity?: string; workDescription?: string; regularHours: string; overtimeHours?: string }>();

  const addMutation = useMutation({
    mutationFn: (v: { entryDate: string; startTime?: string; endTime?: string; projectId?: string; programmeId?: string; activity?: string; workDescription?: string; regularHours: string; overtimeHours?: string }) =>
      timesheetApi.addEntry(timesheetId, {
        entryDate: v.entryDate, startTime: v.startTime || undefined, endTime: v.endTime || undefined,
        projectId: v.projectId ? Number(v.projectId) : undefined, programmeId: v.programmeId ? Number(v.programmeId) : undefined,
        activity: v.activity || undefined, workDescription: v.workDescription || undefined,
        regularHours: Number(v.regularHours), overtimeHours: v.overtimeHours ? Number(v.overtimeHours) : undefined,
      }),
    onSuccess: () => { showSuccess('Entry added.'); queryClient.invalidateQueries({ queryKey: ['timesheet-entries', timesheetId] }); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to add entry.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (entryId: number) => timesheetApi.deleteEntry(timesheetId, entryId),
    onSuccess: () => { showSuccess('Entry removed.'); queryClient.invalidateQueries({ queryKey: ['timesheet-entries', timesheetId] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to remove entry.'),
  });

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <h3>Time Entries</h3>
      {editable && (
        <form className="inline-form" onSubmit={handleSubmit((v) => addMutation.mutate(v))}>
          <label>Date<input type="date" {...register('entryDate', { required: true })} />{errors.entryDate && <span className="field-error">Required</span>}</label>
          <label>Start<input type="time" {...register('startTime')} /></label>
          <label>End<input type="time" {...register('endTime')} /></label>
          <label>
            Project
            <select {...register('projectId')}>
              <option value="">- none -</option>
              {projectsQuery.data?.data.map((p) => <option key={p.id} value={p.id}>{p.project_name}</option>)}
            </select>
          </label>
          <label>
            Programme
            <select {...register('programmeId')}>
              <option value="">- none -</option>
              {programmesQuery.data?.data.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
          <label>Activity<input {...register('activity')} /></label>
          <label>Regular Hours<input type="number" step="0.5" {...register('regularHours', { required: true })} />{errors.regularHours && <span className="field-error">Required</span>}</label>
          <label>Overtime<input type="number" step="0.5" {...register('overtimeHours')} /></label>
          <button type="submit" disabled={addMutation.isPending}>Add Entry</button>
        </form>
      )}
      {entriesQuery.data && (
        <DataTable
          rows={entriesQuery.data.data}
          rowKey={(e) => e.id}
          emptyMessage="No entries recorded yet."
          columns={[
            { header: 'Date', render: (e) => e.entry_date },
            { header: 'Time', render: (e) => e.start_time && e.end_time ? `${e.start_time}-${e.end_time}` : '-' },
            { header: 'Project', render: (e) => e.project_name ?? e.programme_name ?? '-' },
            { header: 'Activity', render: (e) => e.activity ?? '-' },
            { header: 'Hours', render: (e) => `${e.regular_hours}${Number(e.overtime_hours) > 0 ? ` +${e.overtime_hours} OT` : ''}` },
            { header: '', render: (e) => editable && <IconActionButton icon={Trash2} label="Remove" danger onClick={() => deleteMutation.mutate(e.id)} /> },
          ]}
        />
      )}
    </div>
  );
}
